import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes, Navigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider, useAuth } from "@/hooks/useAuth";
import { addBreadcrumb } from "@/lib/sentry";
import Landing from "./pages/Landing";
import Auth from "./pages/Auth";
import Wallet from "./pages/Wallet";
import Merchant from "./pages/Merchant";
import MerchantDashboard from "./pages/MerchantDashboard";
import MerchantOnboarding from "./pages/MerchantOnboarding";
import MerchantCharge from "./pages/MerchantCharge";
import Pay from "./pages/Pay";
import Scan from "./pages/Scan";
import Discover from "./pages/Discover";
import Gigs from "./pages/Gigs";
import NewGig from "./pages/NewGig";
import GigDetail from "./pages/GigDetail";
import MyGigs from "./pages/MyGigs";
import BusinessDashboard from "./pages/BusinessDashboard";
import Disputes from "./pages/Disputes";
import Verification from "./pages/Verification";
import AdminDisputes from "./pages/admin/AdminDisputes";
import Send from "./pages/Send";
import Receive from "./pages/Receive";
import History from "./pages/History";
import Settings from "./pages/Settings";
import CreatorProfile from "./pages/CreatorProfile";
import Request from "./pages/Request";
import PayRequest from "./pages/PayRequest";
import Spin from "./pages/Spin";
import Withdraw from "./pages/Withdraw";
import Vaults from "./pages/Vaults";
import Family from "./pages/Family";
import BillSplit from "./pages/BillSplit";
import Discipline from "./pages/Discipline";
import PointsActivity from "./pages/PointsActivity";
import MyReputation from "./pages/MyReputation";
import CreatorMode from "./pages/CreatorMode";
import CreatorHunts from "./pages/CreatorHunts";
import HuntScan from "./pages/HuntScan";
import IsleFund from "./pages/IsleFund";
import IsleFundNew from "./pages/IsleFundNew";
import IsleFundDetail from "./pages/IsleFundDetail";
import CreatorMissions from "./pages/CreatorMissions";
import CreatorEvents from "./pages/CreatorEvents";
import CreatorQuests from "./pages/CreatorQuests";
import CreatorCircles from "./pages/CreatorCircles";
import CreatorFans from "./pages/CreatorFans";
import CreatorMomentum from "./pages/CreatorMomentum";
import CreatorAI from "./pages/CreatorAI";
import AdminReferrals from "./pages/AdminReferrals";
import AccessDenied from "./pages/AccessDenied";
import { AdminLayout } from "./components/admin/AdminLayout";
import { RoleProtectedRoute } from "./components/RoleProtectedRoute";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminAuditLog from "./pages/admin/AdminAuditLog";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminTransactions from "./pages/admin/AdminTransactions";
import AdminWithdrawals from "./pages/admin/AdminWithdrawals";
import AdminSystem from "./pages/admin/AdminSystem";
import AdminRewards from "./pages/admin/AdminRewards";
import AdminFraud from "./pages/admin/AdminFraud";
import AdminMerchants from "./pages/admin/AdminMerchants";
import AdminKyc from "./pages/admin/AdminKyc";
import AdminReports from "./pages/admin/AdminReports";
import AdminHustleHub from "./pages/admin/AdminHustleHub";
import Kyc from "./pages/Kyc";
import NotFound from "./pages/NotFound";
import Terms from "./pages/legal/Terms";
import Privacy from "./pages/legal/Privacy";
import Refunds from "./pages/legal/Refunds";
import Acceptable from "./pages/legal/Acceptable";
import Escrow from "./pages/legal/Escrow";
import DisputesPolicy from "./pages/legal/Disputes";
import Support from "./pages/Support";
import FAQ from "./pages/FAQ";
import DeleteAccount from "./pages/DeleteAccount";
import LaunchReadiness from "./pages/LaunchReadiness";
import SendNearby from "./pages/SendNearby";
import ReceiveNearby from "./pages/ReceiveNearby";
import AdminBluetooth from "./pages/admin/AdminBluetooth";
import Ads from "./pages/Ads";
import AdminAds from "./pages/admin/AdminAds";
import { installAutoSync } from "./lib/ble/queue";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Waitlist from "./pages/Waitlist";
import BusinessDirectory from "./pages/BusinessDirectory";
import AdminWaitlist from "./pages/admin/AdminWaitlist";

if (typeof window !== "undefined") installAutoSync();

const queryClient = new QueryClient();

const Protected = ({ children }: { children: JSX.Element }) => {
  const { session, loading } = useAuth();
  if (loading) return <div className="min-h-screen grid place-items-center text-muted-foreground">Loading…</div>;
  if (!session) return <Navigate to="/auth" replace />;
  return children;
};

const PublicOnly = ({ children }: { children: JSX.Element }) => {
  const { session, loading } = useAuth();
  if (loading) return <div className="min-h-screen grid place-items-center text-muted-foreground">Loading…</div>;
  if (session) return <Navigate to="/wallet" replace />;
  return children;
};

/** Tracks every route change as a Sentry breadcrumb. */
const NavigationTracker = () => {
  const location = useLocation();
  useEffect(() => {
    addBreadcrumb({
      category: "navigation",
      message: `Navigated to ${location.pathname}`,
      level: "info",
      data: { pathname: location.pathname, search: location.search },
    });
  }, [location]);
  return null;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <NavigationTracker />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/auth" element={<PublicOnly><Auth /></PublicOnly>} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/waitlist" element={<Waitlist />} />
            <Route path="/businesses" element={<BusinessDirectory />} />
            <Route path="/wallet" element={<Protected><Wallet /></Protected>} />
            <Route path="/merchant" element={<Protected><Merchant /></Protected>} />
            <Route path="/merchant/dashboard" element={<Protected><MerchantDashboard /></Protected>} />
            <Route path="/merchant/apply" element={<Protected><MerchantOnboarding /></Protected>} />
            <Route path="/merchant/charge" element={<Protected><MerchantCharge /></Protected>} />
            <Route path="/scan" element={<Protected><Scan /></Protected>} />
            <Route path="/discover" element={<Protected><Discover /></Protected>} />
            <Route path="/gigs" element={<Protected><Gigs /></Protected>} />
            <Route path="/gigs/new" element={<Protected><NewGig /></Protected>} />
            <Route path="/gigs/:id" element={<Protected><GigDetail /></Protected>} />
            <Route path="/my-gigs" element={<Protected><MyGigs /></Protected>} />
            <Route path="/business" element={<Protected><BusinessDashboard /></Protected>} />
            <Route path="/disputes" element={<Protected><Disputes /></Protected>} />
            <Route path="/verification" element={<Protected><Verification /></Protected>} />
            <Route path="/pay/:cashtag" element={<Pay />} />
            <Route path="/send" element={<Protected><Send /></Protected>} />
            <Route path="/send-nearby" element={<Protected><SendNearby /></Protected>} />
            <Route path="/receive-nearby" element={<Protected><ReceiveNearby /></Protected>} />
            <Route path="/receive" element={<Protected><Receive /></Protected>} />
            <Route path="/history" element={<Protected><History /></Protected>} />
            <Route path="/settings" element={<Protected><Settings /></Protected>} />
            <Route path="/request" element={<Protected><Request /></Protected>} />
            <Route path="/spin" element={<Protected><Spin /></Protected>} />
            <Route path="/withdraw" element={<Protected><Withdraw /></Protected>} />
            <Route path="/vaults" element={<Protected><Vaults /></Protected>} />
            <Route path="/family" element={<Protected><Family /></Protected>} />
            <Route path="/bills" element={<Protected><BillSplit /></Protected>} />
            <Route path="/discipline" element={<Protected><Discipline /></Protected>} />
            <Route path="/points" element={<Protected><PointsActivity /></Protected>} />
            <Route path="/my-reputation" element={<Protected><MyReputation /></Protected>} />
            <Route path="/creator-mode" element={<Protected><CreatorMode /></Protected>} />
            <Route path="/creator-mode/hunts" element={<Protected><CreatorHunts /></Protected>} />
            <Route path="/creator-mode/missions" element={<Protected><CreatorMissions /></Protected>} />
            <Route path="/creator-mode/events" element={<Protected><CreatorEvents /></Protected>} />
            <Route path="/creator-mode/quests" element={<Protected><CreatorQuests /></Protected>} />
            <Route path="/creator-mode/circles" element={<Protected><CreatorCircles /></Protected>} />
            <Route path="/creator-mode/fans" element={<Protected><CreatorFans /></Protected>} />
            <Route path="/creator-mode/momentum" element={<Protected><CreatorMomentum /></Protected>} />
            <Route path="/creator-mode/ai" element={<Protected><CreatorAI /></Protected>} />
            <Route path="/hunt/:code" element={<HuntScan />} />
            <Route path="/isle-fund" element={<Protected><IsleFund /></Protected>} />
            <Route path="/isle-fund/new" element={<Protected><IsleFundNew /></Protected>} />
            <Route path="/isle-fund/:id" element={<Protected><IsleFundDetail /></Protected>} />
            <Route path="/access-denied" element={<AccessDenied />} />
            <Route path="/kyc" element={<Protected><Kyc /></Protected>} />
            <Route path="/ads" element={<Protected><Ads /></Protected>} />

            {/* Admin console */}
            <Route
              path="/admin"
              element={
                <RoleProtectedRoute
                  allowedRoles={[
                    "admin",
                    "moderator",
                    "super_admin",
                    "finance_admin",
                    "support_admin",
                    "merchant_admin",
                  ]}
                >
                  <AdminLayout />
                </RoleProtectedRoute>
              }
            >
              <Route index element={<AdminDashboard />} />
              <Route path="referrals" element={<AdminReferrals />} />
              <Route path="audit" element={<AdminAuditLog />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="transactions" element={<AdminTransactions />} />
              <Route path="kyc" element={<AdminKyc />} />
              <Route path="rewards" element={<AdminRewards />} />
              <Route path="withdrawals" element={<AdminWithdrawals />} />
              <Route path="fraud" element={<AdminFraud />} />
              <Route path="disputes" element={<AdminDisputes />} />
              <Route path="merchants" element={<AdminMerchants />} />
              <Route path="reports" element={<AdminReports />} />
              <Route path="hustle-hub" element={<AdminHustleHub />} />
              <Route path="bluetooth" element={<AdminBluetooth />} />
              <Route path="ads" element={<AdminAds />} />
              <Route path="waitlist" element={<AdminWaitlist />} />
              <Route path="system" element={<AdminSystem />} />
            </Route>

            <Route path="/r/:id" element={<PayRequest />} />
            <Route path="/u/:cashtag" element={<CreatorProfile />} />
            <Route path="/legal/terms" element={<Terms />} />
            <Route path="/legal/privacy" element={<Privacy />} />
            <Route path="/legal/refunds" element={<Refunds />} />
            <Route path="/legal/acceptable-use" element={<Acceptable />} />
            <Route path="/legal/escrow" element={<Escrow />} />
            <Route path="/legal/disputes" element={<DisputesPolicy />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/support" element={<Support />} />
            <Route path="/settings/delete-account" element={<DeleteAccount />} />
            <Route path="/launch-readiness" element={<LaunchReadiness />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
